// ✅ React and Related
import React, { Fragment, useMemo } from 'react';
import { Link } from 'react-router-dom';

// ✅ Ant Design Components
import { Table, Tag } from 'antd';
import { SwapLeftOutlined, SwapRightOutlined } from '@ant-design/icons';

// ✅ Constants
import { QUANTITY, DEFAULT_CUR_ROUND_OFF } from '@Apis/constants';

// ✅ Custom UI Components
import H3Text from '@Uilib/h3Text';
import ProductCategoryLabel from '../../../../Common/ProductCategoryLabel';
import HideValue from '../../../../Common/RestrictedAccess/HideValue';

// ✅ Helpers
import CustomFieldHelpers from '../../../../../helpers/CustomFieldHelpers';

// ✅ Styles
import './style.scss';

function CreditNoteLines ({
  cnLines,
  selectedCN,
  isReturnSlip,
  priceMasking,
  MONEY,
  selectedPurchaseOrder,
  cfCreditNoteLine,
}) {

  const { isDataMaskingPolicyEnable, isHideSellingPrice } = priceMasking;

  const cnLinesColumns = useMemo(() => {
    const columns =
    [{
      title: '',
      render: (record) => (
        <div
          className="mobile-list__item"
        >
          <Link to={`/inventory/product/view/${record?.product_sku_info?.product_sku_id}`} target="_blank">

            <div style={{ display: 'flex' }}>
              <H3Text text={`#${record?.product_sku_info?.product_sku_name}`} className="mobile-list__item-number" />
            </div>
            <div style={{ marginBottom: '5px' }}>
              {record?.adjustment_entity_type?.replaceAll('_', ' ') === 'OUTWARD'
                  && (
                    <div className="ia-type-tag" style={{ backgroundColor: '#f15353' }}>
                      <SwapRightOutlined />
                      <H3Text text="Outgoing" className="" />
                    </div>
                  )}
              {record?.adjustment_entity_type?.replaceAll('_', ' ') === 'INWARDS'
                  && (
                    <div className="ia-type-tag" style={{ backgroundColor: '#2fbc6e' }}>
                      <SwapLeftOutlined />
                      <H3Text text="Incoming" className="" />
                    </div>
                  )}
            </div>
            <div className="mobile-list__item-info">
              <Tag color="processing">
                {QUANTITY(record?.quantity, record?.uom_info?.[0]?.precision)}
                {' '}
                {record?.uom_info?.[0]?.uqc?.toProperCase() || ''}
              </Tag>
            </div>
            <div className="flex-align-space-between">

              <H3Text text={`${MONEY((record.unit_price), selectedCN?.org_currency_info?.currency_code)} Per Pcs`} className="mobile-list__item-info" />
              <H3Text text={MONEY((record.line_total_price || '0'), selectedCN?.org_currency_info?.currency_code)} className="mobile-list__item-info" />
            </div>
            <H3Text text={`Tax@ ${record.tax_info?.tax_value}%`} className="mobile-list__item-info" />
          </Link>
        </div>

      ),
      responsive: ['xs'],
    },
    {
      title: selectedCN?.without_lines ? 'Product/Reason' : 'Product',
      width: '220px',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (item) => (
        <Fragment>
          {item?.product_sku_info?.product_sku_id && (
            <Link to={`/inventory/product/view/${item?.product_sku_info?.product_sku_id}?tab=1`} target="_blank">
              <H3Text text={`#${item?.product_sku_info?.internal_sku_code}`} className="mobile-list__item-number" />
            </Link>
          )}
          {item?.product_sku_info?.product_sku_name || item?.product_sku_name}
          {item?.product_category_info?.category_path?.length > 0 && (
            <ProductCategoryLabel
              categoryPath={item?.product_category_info?.category_path}
              categoryName={item?.product_category_info?.category_path?.at(-1)}
              containerStyle={{
                width: 'fit-content',
              }}
            />
          )}
          <H3Text text={item.remarks} className="table-subscript" />
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div>
              {(item?.product_sku_info?.product_type === 'BUNDLE') && <H3Text text="Bundle" className="table-subscript table-subscript-tag" />}
              {(item?.product_sku_info?.product_type === 'SERVICE') && <H3Text text="Service" className="table-subscript table-subscript-tag" />}
              {(!['SERVICE', 'BUNDLE'].includes(item?.product_sku_info?.product_type)) && <H3Text text="Goods" className="table-subscript table-subscript-tag" />}
            </div>
            {item?.hsn_code && (
              <div style={{
                fontSize: '10px', display: 'flex', marginLeft: '5px', marginTop: '7px',
              }}
              >
                <H3Text text={item?.product_sku_info?.product_type === 'SERVICE' ? 'SAC - ' : 'HSN - '} />
                  &nbsp;
                <div className="invoice-table__hsn-value">
                  <H3Text text={item?.hsn_code} />
                </div>
              </div>
            )}
          </div>
        </Fragment>
      ),
    },
    {
      title: 'Quantity',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (item) => `${QUANTITY(item?.quantity, item?.uom_info?.[0]?.precision)} ${item?.uom_info?.[0]?.uqc?.toProperCase()}`,
      hidden: selectedCN?.without_lines,
    },
    {
      title: 'Returned Quantity',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (item) => `${QUANTITY(item?.returned_quantity, item?.uom_info?.[0]?.precision)} ${item?.uom_info?.[0]?.uqc?.toProperCase()}`,
      hidden: selectedCN?.without_lines,
    },
    {
      title: 'Price',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => ((isDataMaskingPolicyEnable && isHideSellingPrice) ? <HideValue showPopOver popOverMessage={"You don't have access to view price"} /> : MONEY((record.unit_price), selectedCN?.org_currency_info?.currency_code)),
      hidden: isReturnSlip,
    },
    {
      title: 'Tax',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => `${record?.tax_group_info?.tax_value}%`,
      hidden: isReturnSlip,
    },
    {
      title: 'Discount',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => (
        <Fragment>
          {Number(record?.line_discount_amount) > 0 ? (
            <Fragment>
              {record?.is_discount_in_percent ? `${Number(record?.line_discount_percentage).toPrecision(DEFAULT_CUR_ROUND_OFF) || '0'}%` : MONEY(record?.line_discount_amount, selectedCN?.org_currency_info?.currency_code)}
            </Fragment>
          ) : '-'}
        </Fragment>
      ),
      hidden: isReturnSlip,
    },
    {
      title: 'Total',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => ((isDataMaskingPolicyEnable && isHideSellingPrice) ? <HideValue showPopOver popOverMessage={"You don't have access to view total"} /> : MONEY(((record.unit_price * record.quantity) * (record.line_discount_percentage ? Number(100 - record.line_discount_percentage) / 100 : 1)), selectedCN?.org_currency_info?.currency_code)),
      hidden: isReturnSlip,
    },
    {
      title: 'Total (Incl. Tax)',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => ((isDataMaskingPolicyEnable && isHideSellingPrice) ? <HideValue showPopOver popOverMessage={"You don't have access to view total (incl. tax)"} /> : MONEY((record.line_total_price || '0'), selectedCN?.org_currency_info?.currency_code)),
      hidden: isReturnSlip,
    },
    ]?.filter((item) => !item?.hidden);
    if(cfCreditNoteLine?.length) {
      columns.splice(6, 0, ...CustomFieldHelpers.renderCustomLineColumns(false, cfCreditNoteLine, {}, null));
    }
    return columns;
  });

  return (
    <Fragment>
      <div className="cn-table-wrapper">
        <Table
          bordered
          showHeader
          title={() => `${isReturnSlip ? 'Return Slip' : 'Credit Note'} Items (${cnLines?.length})`}
          size="small"
          columns={(selectedPurchaseOrder?.status === 'RECEIVED' || selectedPurchaseOrder?.status === 'PARTIALLY_RECEIVED') ? cnLinesColumns : cnLinesColumns.filter((item) => item.title !== 'Received Quantity')}
          // dataSource={cnLines || []}
          dataSource={cnLines?.map((item) => ({
            ...item,
            product_category_info: item?.product_sku_info?.product_category_info,
          })) || []}
          pagination={false}
          scroll={{ x: true }}
        />
      </div>
    </Fragment>
  );
}

export default CreditNoteLines;
